import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Modal, Field, fieldInputClass } from "@/ui/modals/Modal";
import { Button } from "@/ui/Button";
import type {
  Client,
  ClientStatus,
  ClientType,
  ClientLevel,
  ContractModel,
  Currency,
  ServiceType,
} from "@/types";
import { useLabels } from "@/lib/labels";

const optionalText = z.string().trim().default("");
const optionalEmail = z.union([z.string().trim().email("Email invalide"), z.literal("")]).default("");

const clientSchema = z.object({
  name: z.string().trim().min(2, "Nom requis"),
  email: optionalEmail,
  phone: optionalText,
  type: z.enum(["SERVICE", "PARTNERSHIP"]),
  niche: optionalText,
  level: z.enum(["BEGINNER", "INTERMEDIATE", "EXPERT"]),
  status: z.enum(["ACTIVE", "ONBOARDING", "PAUSED", "CHURNED"]),
  contractModel: z.enum(["MONTHLY_SUBSCRIPTION", "DELIVERY_ORDER", "COMMISSION", "DRAFT"]),
  service: z.enum([
    "MEDIA_BUYER",
    "CREATIVE",
    "MARKETING_STRATEGIST",
    "AI_AGENT",
    "AUTOMATION",
    "WEBSITE_SAAS",
  ]),
  monthlyFee: z.coerce.number().min(0).default(0),
  currency: z.enum(["DZD", "USD", "EUR"]).default("DZD"),
  startDate: optionalText,
  facebookUrl: optionalText,
  instagramUrl: optionalText,
  tiktokUrl: optionalText,
  notes: optionalText,
});

export type ClientFormValues = z.infer<typeof clientSchema>;

const EMPTY: ClientFormValues = {
  name: "",
  email: "",
  phone: "",
  type: "SERVICE",
  niche: "",
  level: "BEGINNER",
  status: "ONBOARDING",
  contractModel: "DRAFT",
  service: "MEDIA_BUYER",
  monthlyFee: 0,
  currency: "DZD",
  startDate: "",
  facebookUrl: "",
  instagramUrl: "",
  tiktokUrl: "",
  notes: "",
};

function options<T extends string>(map: Record<T, { label: string }>) {
  return (Object.keys(map) as T[]).map((k) => ({ value: k, label: map[k].label }));
}

function toInputDate(value: string | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 10);
  return date.toISOString().slice(0, 10);
}

export function ClientFormModal({
  open,
  onClose,
  onSave,
  initial,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (values: ClientFormValues) => void;
  initial?: Client;
}) {
  const { t } = useTranslation();
  const { CLIENT_STATUS, CLIENT_TYPE, CLIENT_LEVEL, CONTRACT_MODEL, SERVICE_TYPE } = useLabels();
  const [values, setValues] = useState<ClientFormValues>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setErrors({});
      setValues(
        initial
          ? {
              name: initial.name,
              email: initial.email ?? "",
              phone: initial.phone ?? "",
              type: initial.type,
              niche: initial.niche ?? "",
              level: initial.level ?? "BEGINNER",
              status: initial.status,
              contractModel: initial.contractModel,
              service: initial.service,
              monthlyFee: initial.monthlyFee,
              currency: initial.currency ?? "DZD",
              startDate: toInputDate(initial.startDate),
              facebookUrl: initial.facebookUrl ?? "",
              instagramUrl: initial.instagramUrl ?? "",
              tiktokUrl: initial.tiktokUrl ?? "",
              notes: initial.notes ?? "",
            }
          : EMPTY,
      );
    }
  }, [open, initial]);

  const set = <K extends keyof ClientFormValues>(key: K, value: ClientFormValues[K]) =>
    setValues((v) => ({ ...v, [key]: value }));

  const submit = () => {
    const parsed = clientSchema.safeParse(values);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string") fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    onSave(parsed.data);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? t("clients.form.editTitle") : t("clients.form.addTitle")}
      maxWidth={720}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button size="sm" onClick={submit}>
            {initial ? t("common.save") : t("clients.add")}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3">
        <Field label={t("clients.form.name")} required error={errors.name}>
          <input className={fieldInputClass} value={values.name} onChange={(e) => set("name", e.target.value)} />
        </Field>
        <Field label={t("clients.form.email")} error={errors.email}>
          <input className={fieldInputClass} value={values.email} onChange={(e) => set("email", e.target.value)} />
        </Field>
        <Field label={t("clients.form.phone")} error={errors.phone}>
          <input className={fieldInputClass} value={values.phone} onChange={(e) => set("phone", e.target.value)} />
        </Field>
        <Field label={t("clients.form.niche")} error={errors.niche}>
          <input className={fieldInputClass} value={values.niche} onChange={(e) => set("niche", e.target.value)} />
        </Field>
        <Field label={t("clients.form.type")} required>
          <select className={fieldInputClass} value={values.type} onChange={(e) => set("type", e.target.value as ClientType)}>
            {options(CLIENT_TYPE).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("clients.form.level")} required>
          <select className={fieldInputClass} value={values.level} onChange={(e) => set("level", e.target.value as ClientLevel)}>
            {options(CLIENT_LEVEL).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("clients.form.status")} required>
          <select className={fieldInputClass} value={values.status} onChange={(e) => set("status", e.target.value as ClientStatus)}>
            {options(CLIENT_STATUS).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("clients.form.service")} required>
          <select className={fieldInputClass} value={values.service} onChange={(e) => set("service", e.target.value as ServiceType)}>
            {options(SERVICE_TYPE).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("clients.form.contractModel")} required>
          <select className={fieldInputClass} value={values.contractModel} onChange={(e) => set("contractModel", e.target.value as ContractModel)}>
            {options(CONTRACT_MODEL).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("clients.form.monthlyFee")} error={errors.monthlyFee}>
          <input
            type="number"
            className={fieldInputClass}
            value={values.monthlyFee}
            onChange={(e) => set("monthlyFee", Number(e.target.value))}
          />
        </Field>
        <Field label={t("clients.form.currency")}>
          <select className={fieldInputClass} value={values.currency} onChange={(e) => set("currency", e.target.value as Currency)}>
            {(["DZD", "USD", "EUR"] as Currency[]).map((currency) => (
              <option key={currency} value={currency}>
                {currency}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("clients.form.startDate")} error={errors.startDate}>
          <input
            type="date"
            className={fieldInputClass}
            value={values.startDate}
            onChange={(e) => set("startDate", e.target.value)}
          />
        </Field>
        <Field label={t("clients.facebookLink")} error={errors.facebookUrl}>
          <input className={fieldInputClass} value={values.facebookUrl} onChange={(e) => set("facebookUrl", e.target.value)} />
        </Field>
        <Field label={t("clients.instagramLink")} error={errors.instagramUrl}>
          <input className={fieldInputClass} value={values.instagramUrl} onChange={(e) => set("instagramUrl", e.target.value)} />
        </Field>
        <Field label={t("clients.tiktokLink")} error={errors.tiktokUrl}>
          <input className={fieldInputClass} value={values.tiktokUrl} onChange={(e) => set("tiktokUrl", e.target.value)} />
        </Field>
      </div>
      <Field label={t("clients.form.notes")} error={errors.notes}>
        <textarea
          className={`${fieldInputClass} min-h-[76px] resize-y`}
          value={values.notes}
          onChange={(e) => set("notes", e.target.value)}
        />
      </Field>
    </Modal>
  );
}
